import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Divider } from '@mui/material';
import { Print, ArrowBack } from '@mui/icons-material';
import { insuranceCaseService, insuranceDocumentTemplateService, printSettingsService } from '../../services/api';

const InsuranceDocumentTemplatePrint = () => {
  const { caseId, templateId } = useParams();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [template, setTemplate] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add('consent-print-page');
    return () => document.body.classList.remove('consent-print-page');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, tRes, psRes] = await Promise.all([
          insuranceCaseService.getPacket(caseId),
          insuranceDocumentTemplateService.getById(templateId),
          printSettingsService.getSettings(),
        ]);
        setPacket(pRes.data);
        setTemplate(tRes.data);
        setPrintSettings(psRes.data);
      } catch {
        alert('Failed to load document');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId, templateId]);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const diff = new Date() - new Date(dob);
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : '___';

  const formatAmount = (a) => (a === null || a === undefined) ? '___' : `Rs. ${Number(a).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  // Seeded system templates store placeholders as single braces (e.g. {PatientName}) because
  // EF Core's ExecuteSqlRaw runs the seed SQL through string.Format, collapsing {{X}} to {X}.
  // Match both {{X}} and {X} so user-authored templates using either style still work.
  const placeholder = (name) => new RegExp(`\\{\\{?${name}\\}\\}?`, 'g');

  const renderTemplateText = (text, p, ps) => {
    if (!text) return '';
    const patient = p?.patient;
    return text
      .replace(placeholder('PatientName'),         patient ? `${patient.firstName} ${patient.lastName}` : '___')
      .replace(placeholder('PatientAge'),          patient?.dateOfBirth ? String(calculateAge(patient.dateOfBirth)) : '___')
      .replace(placeholder('PatientGender'),       patient?.gender || '___')
      .replace(placeholder('PatientAddress'),      patient?.address || '___')
      .replace(placeholder('PatientPhone'),        patient?.phoneNumber || '___')
      .replace(placeholder('AadharNumber'),        patient?.aadharNumber || '___')
      .replace(placeholder('PolicyNumber'),        p?.policyNumber || '___')
      .replace(placeholder('MemberId'),            p?.memberId || '___')
      .replace(placeholder('InsurerName'),         p?.insurerName || '___')
      .replace(placeholder('TPAName'),             p?.tpaName || '___')
      .replace(placeholder('ClaimType'),           p?.claimType || '___')
      .replace(placeholder('AdmissionDate'),       formatDate(p?.admission?.admissionDate))
      .replace(placeholder('DischargeDate'),       formatDate(p?.admission?.actualDischargeDate))
      .replace(placeholder('AdmissionDiagnosis'),  p?.admission?.admissionDiagnosis || '___')
      .replace(placeholder('Icd10Code'),           p?.admission?.icd10Code || '___')
      .replace(placeholder('WardType'),            p?.admission?.wardType || '___')
      .replace(placeholder('AdmittingDoctorName'), p?.admission?.admittingDoctorName || '___')
      .replace(placeholder('EstimatedAmount'),     formatAmount(p?.preAuthAmount))
      .replace(placeholder('ApprovedAmount'),      formatAmount(p?.approvedAmount))
      .replace(placeholder('SettledAmount'),       formatAmount(p?.settledAmount))
      .replace(placeholder('ClaimNumber'),         p?.claimNumber || '___')
      .replace(placeholder('TpaReferenceNumber'),  p?.tpaReferenceNumber || '___')
      .replace(placeholder('HospitalName'),        ps?.hospitalName || 'Vedanta Hospital')
      .replace(placeholder('HospitalAddress'),     ps?.address || '___');
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  );

  if (!packet || !template) return <Box sx={{ p: 4 }}><Typography>Document not found.</Typography></Box>;

  const ps = printSettings || {};
  const mt = ps.marginTop ?? 10;
  const mb = ps.marginBottom ?? 10;
  const ml = ps.marginLeft ?? 10;
  const mr = ps.marginRight ?? 10;

  const processedBody = renderTemplateText(template.templateBody || '', packet, ps);

  return (
    <Box>
      {/* Print controls — hidden when printing */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', gap: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <Button startIcon={<ArrowBack />} onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}>Back</Button>
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
      </Box>

      {/* ── Printable document ── */}
      <Box sx={{
        maxWidth: '210mm',
        margin: '0 auto',
        p: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
        fontFamily: '"Times New Roman", serif',
        fontSize: '13px',
        lineHeight: 1.6,
        bgcolor: 'white',
        '@media print': {
          margin: 0,
          maxWidth: 'none',
          padding: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
          boxShadow: 'none',
        },
      }}>

        {/* Hospital header — only for non-preprinted letterheads */}
        {ps.letterheadType !== 'Pre-Printed Letterhead' && (
          <>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, fontFamily: 'inherit' }}>
                {ps.hospitalName || 'Vedanta Hospital'}
              </Typography>
              {ps.address && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>{ps.address}</Typography>}
              {ps.phone && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>Tel: {ps.phone}</Typography>}
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#333', borderWidth: 1 }} />
          </>
        )}

        {/* Document body */}
        <Box
          dangerouslySetInnerHTML={{ __html: processedBody }}
          sx={{
            fontFamily: 'inherit', fontSize: '13px', textAlign: 'justify',
            '& p': { marginTop: 0, marginBottom: '0.6em' },
            '& table': { borderCollapse: 'collapse', width: '100%' },
            '& td': { border: '1px solid #999', padding: '4px' },
            '& ol, & ul': { paddingLeft: '1.5em', marginTop: 0, marginBottom: '0.6em' },
            '& li': { marginBottom: '0.25em' },
            '& h1, & h2, & h3': { fontFamily: 'inherit', marginTop: '0.5em', marginBottom: '0.3em' },
            '& hr': { border: 'none', borderTop: '1px solid #999', margin: '10px 0', pageBreakAfter: 'avoid' },
          }}
        />

      </Box>{/* end printable document */}

      <style>{`
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default InsuranceDocumentTemplatePrint;
